/**
 * JT Goggle Hub grid behaviour.
 * - Quick filter pills: active state and in-place filtering through Dawn's facets.
 * - The floating mobile "Filter and sort" pill only shows while the product grid is on screen.
 * - "Best seller" chips from Shopify's own best-selling order (one cached request per session).
 * - Live countdowns for elements with data-kz-countdown (real end dates only; hidden once passed).
 * - Leaves the bundles listed in data-kz-hide-bundles out of the Kore Bundles cards.
 * All re-apply after facets.js re-renders the grid.
 */
(function () {
  // Kore Bundles cards: the app builds them on DOMContentLoaded from #kore-bundle-showcase-data,
  // and this deferred script runs first, so dropping a bundle here keeps it out of the grid
  // (and out of collection-grid-fill's slot count).
  function hideBundles() {
    const section = document.querySelector('[data-kz-hide-bundles]');
    const data = document.getElementById('kore-bundle-showcase-data');
    if (!section || !data) return;
    const hidden = section.dataset.kzHideBundles
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    const handleOf = (url) => (url || '').split('?')[0].split('/').pop().toLowerCase();
    try {
      const bundles = JSON.parse(data.textContent) || [];
      const kept = bundles.filter(
        (bundle) => !hidden.includes(String(bundle.tag || '').toLowerCase()) && !hidden.includes(handleOf(bundle.url))
      );
      if (kept.length !== bundles.length) data.textContent = JSON.stringify(kept);
    } catch (error) {
      /* leave the app's data untouched */
    }
  }

  hideBundles();

  // Quick filter pills in the grid toolbar: active state + in-place filtering through Dawn's facets.
  function filterKey(search) {
    const pairs = [];
    new URLSearchParams(search).forEach((value, key) => {
      if (key.indexOf('filter.') === 0) pairs.push(`${key}=${value}`);
    });
    return pairs.sort().join('&');
  }

  function markPills() {
    const current = filterKey(window.location.search);
    document.querySelectorAll('[data-kz-qf-pill]').forEach((pill) => {
      const url = new URL(pill.href, window.location.origin);
      const active = url.pathname === window.location.pathname && filterKey(url.search) === current;
      if (active) {
        pill.setAttribute('aria-current', 'page');
      } else {
        pill.removeAttribute('aria-current');
      }
    });
  }

  document.addEventListener('click', (event) => {
    const pill = event.target.closest('[data-kz-qf-pill]');
    if (!pill || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(pill.href, window.location.origin);
    if (url.pathname !== window.location.pathname || typeof FacetFiltersForm === 'undefined') return;
    event.preventDefault();
    const params = new URLSearchParams(url.search);
    params.delete('page');
    const sort = new URLSearchParams(window.location.search).get('sort_by');
    if (sort && !params.has('sort_by')) params.set('sort_by', sort);
    FacetFiltersForm.renderPage(params.toString());
    markPills();
    // Shortcuts outside the toolbar (category banner) take the shopper to the filtered grid.
    if (!pill.closest('.kz-toolbar')) {
      const shop = document.getElementById('kz-hub-shop');
      if (shop) shop.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  window.addEventListener('popstate', markPills);

  // Best seller chips: rank comes from the collection sorted by best-selling.
  const BEST_KEY = `kz-best:${window.location.pathname}`;
  let bestIds = null;

  function readBestCache() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(BEST_KEY) || 'null');
      if (cached && Date.now() - cached.at < 30 * 60 * 1000) return cached.ids;
    } catch (error) {
      /* storage unavailable */
    }
    return null;
  }

  async function loadBestIds(grid) {
    if (bestIds) return bestIds;
    bestIds = readBestCache();
    if (bestIds) return bestIds;
    const url = new URL(window.location.pathname, window.location.origin);
    const view = new URLSearchParams(window.location.search).get('view');
    if (view) url.searchParams.set('view', view);
    url.searchParams.set('sort_by', 'best-selling');
    url.searchParams.set('section_id', grid.dataset.id);
    const response = await fetch(url.toString());
    if (!response.ok) return [];
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    // Ranking ignores the hub's own "in stock first" reorder: Shopify returns best-selling order,
    // and only in-stock items get a chip.
    bestIds = [...doc.querySelectorAll('#product-grid > [data-product-id]')]
      .filter((item) => !item.querySelector('.card__chip--sold-out'))
      .map((item) => item.dataset.productId);
    try {
      sessionStorage.setItem(BEST_KEY, JSON.stringify({ at: Date.now(), ids: bestIds }));
    } catch (error) {
      /* storage unavailable */
    }
    return bestIds;
  }

  async function applyBestSellers() {
    const grid = document.getElementById('product-grid');
    const count = Number(grid && grid.dataset.kzBest);
    if (!grid || !count) return;
    let ids = [];
    try {
      ids = (await loadBestIds(grid)).slice(0, count);
    } catch (error) {
      return;
    }
    ids.forEach((id) => {
      const chips = grid.querySelector(`[data-product-id="${id}"] .card__chips`);
      if (!chips || chips.querySelector('.card__chip--best')) return;
      const chip = document.createElement('span');
      chip.className = 'card__chip card__chip--best';
      chip.textContent = 'Best seller';
      chips.prepend(chip);
    });
  }

  // Countdowns: one ticker for every [data-kz-countdown] on the page.
  function formatLeft(ms) {
    const total = Math.floor(ms / 1000);
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return days > 0 ? `${days}d ${pad(hours)}h ${pad(mins)}m` : `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  }

  function tickCountdowns() {
    document.querySelectorAll('[data-kz-countdown]').forEach((el) => {
      const end = Date.parse(el.dataset.kzCountdown);
      const left = end - Date.now();
      if (Number.isNaN(end) || left <= 0) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      const text = el.querySelector('[data-kz-countdown-text]');
      if (text) text.textContent = formatLeft(left);
    });
  }

  setInterval(tickCountdowns, 1000);

  let observer = null;
  let gridMutations = null;
  function watchGrid() {
    markPills();
    const container = document.getElementById('ProductGridContainer');
    if (container && !gridMutations) {
      gridMutations = new MutationObserver(() => {
        markPills();
        applyBestSellers();
        tickCountdowns();
      });
      gridMutations.observe(container, { childList: true });
    }
    tickCountdowns();
    if ('requestIdleCallback' in window) {
      requestIdleCallback(applyBestSellers, { timeout: 2500 });
    } else {
      setTimeout(applyBestSellers, 1200);
    }
    const section = document.querySelector('.kz-hub-grid');
    const grid = document.getElementById('ProductGridContainer');
    if (!section || !grid || !('IntersectionObserver' in window)) return;
    if (observer) observer.disconnect();
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => section.classList.toggle('kz-grid-out-of-view', !entry.isIntersecting));
      },
      { rootMargin: '-35% 0px -20% 0px' }
    );
    observer.observe(grid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchGrid);
  } else {
    watchGrid();
  }
})();
